"use client";

import {
  isTypeCta,
  isTypeGenericSection,
  TypeCta,
  TypeGenericSection,
  TypeGenericSectionSkeleton,
  TypePageSkeleton,
} from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notEmpty } from "@yardzen/js-util";
import { Entry } from "contentful";
import React, { useActionState } from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "./button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "./form";
import { H2 } from "./h2";
import { Input } from "./input";
import { Label } from "./label";
import { Link } from "./link";
import { PhoneInput } from "./phone-input";
import { Spinner } from "./spinner";

function getGenericSection(
  page: Entry<TypePageSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>,
): TypeGenericSection<"WITHOUT_UNRESOLVABLE_LINKS"> | null {
  return (
    page.fields.sections.filter(notEmpty).filter(isTypeGenericSection)[0] ||
    null
  );
}
function getCta(
  page: Entry<TypeGenericSectionSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>,
): TypeCta<"WITHOUT_UNRESOLVABLE_LINKS"> | null {
  return (
    page.fields.CTAs?.filter(notEmpty)
      .filter(isTypeCta)
      .find((cta) => cta.fields.title === "Quiz Sign Up Page") || null
  );
}

export const EmailLeadCapture = ({
  form,
  segmentFlow,
  segmentForm,
  contentfulPage,
  action,
  ...formProps
}: {
  form: UseFormReturn<any>;
  segmentFlow: string;
  segmentForm: string;
  contentfulPage: Entry<
    TypePageSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  > | null;
  action: (_prevState: unknown, formData: FormData) => Promise<void>;
} & Omit<React.FormHTMLAttributes<HTMLFormElement>, "action">) => {
  const [_, formAction, isPending] = useActionState(action as any, null);

  const emailCapture = contentfulPage
    ? getGenericSection(contentfulPage)
    : null;
  const captureCTA = emailCapture ? getCta(emailCapture) : null;

  const headerCopy =
    emailCapture?.fields.title || "Almost done. Let's save your results.";
  const subCopy = emailCapture?.fields.subtitle;
  const buttonCopy = captureCTA?.fields.buttonText || "Next";
  const disclaimerCopy = captureCTA?.fields.disclaimerText;

  return (
    <div className="flex max-w-[500px] flex-col flex-wrap justify-center gap-4 rounded-lg bg-white p-10 text-center md:flex-row">
      <div>
        <H2>{headerCopy}</H2>
        <p className="text-brand-moss-greens-90 mt-2">{subCopy}</p>
      </div>
      <div className="flex w-full flex-col gap-4">
        <Form {...form}>
          <form
            {...formProps}
            action={formAction}
            className="flex w-full flex-col gap-3"
          >
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="email" className="text-left text-base">
                  Email
                </Label>
                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    required: "Email is required",
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Enter your email address"
                          type="email"
                          {...field}
                          className="w-full flex-1 py-2 text-black"
                          tracking={{
                            flow_name: segmentFlow,
                            form_name: segmentForm,
                            text_value: field.value,
                            field_name: "Email Address",
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2 pt-4">
                <Label htmlFor="phone" className="text-left text-base">
                  Phone Number (Optional)
                </Label>
                <Label className="text-left text-xs">
                  Enter your phone number to receive design tips, inspiration,
                  and offers via text.
                </Label>
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PhoneInput
                          placeholder="Enter your phone number"
                          {...field}
                          className="w-full flex-1 py-2 text-black"
                          tracking={{
                            flow_name: segmentFlow,
                            form_name: segmentForm,
                            text_value: field.value,
                            field_name: "Phone Number",
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <p className="text-brand-moss-greens-90 pb-4 pt-2 text-left text-xs">
                By entering your phone number and submitting this form, you
                agree to receive recurring automated promotional and
                personalized marketing text messages from Yardzen. Reply HELP
                for help and STOP to cancel. Message frequency vary. Message and
                data rates may apply. View{" "}
                <Link
                  href="https://attnl.tv/t/gb1"
                  target="_blank"
                  className="underline"
                >
                  Terms
                </Link>{" "}
                &{" "}
                <Link
                  href="https://attnl.tv/p/gb1"
                  target="_blank"
                  className="underline"
                >
                  Privacy Policy
                </Link>
                .
              </p>
              <p className="text-brand-moss-greens-90 py-2 text-left text-xs">
                By continuing, you agree to Yardzen's{" "}
                <Link className="underline" href="/terms-and-conditions">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link className="underline" href="/privacy-policy">
                  Privacy Policy.
                </Link>
              </p>
              <Button
                type="submit"
                variant="default"
                size="lg"
                disabled={!form.formState.isValid || isPending}
                className="px-4 py-2 hover:bg-gray-100 disabled:bg-gray-200 disabled:text-gray-400"
                endIcon={
                  isPending ? (
                    <Spinner className="size-4" />
                  ) : (
                    <FontAwesomeIcon icon={faArrowRight} />
                  )
                }
              >
                {buttonCopy}
              </Button>
            </div>
          </form>
        </Form>
        <p className="text-brand-moss-greens-90 text-xxs py-4">
          {disclaimerCopy}
        </p>
      </div>
    </div>
  );
};
